import { Injectable, signal, inject, Injector, runInInjectionContext } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  Firestore,
} from '@angular/fire/firestore';
import { DataService } from './data.service';
import { Membership } from '../models/firestore.types';

@Injectable({
  providedIn: 'root',
})
export class MembershipService {
  private injector = inject(Injector);
  private dataService = inject(DataService);
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private functions = inject(Functions);

  userMemberships = signal<Membership[]>([]);
  // Signal to trigger refresh in components
  refreshTrigger = signal(0);
  private memberCache = new Map<
    string,
    { data: Membership[]; timestamp: number }
  >();
  private CACHE_TTL = 60000; // 1 minute cache

  async getUserMemberships(userId?: string): Promise<Membership[]> {
    const uid = userId || this.auth.currentUser?.uid;
    if (!uid) return [];

    const memberships = await this.dataService.list<Membership>('memberships', {
      field: 'userId',
      operator: '==',
      value: uid,
    });

    this.userMemberships.set(memberships);
    return memberships;
  }

  triggerRefresh() {
    this.refreshTrigger.update((v) => v + 1);
    this.memberCache.clear(); // Clear cache on manual refresh
  }

  async getEntityMembers(
    entityType: string,
    entityId: string,
    forceRefresh = false
  ): Promise<Membership[]> {
    const cacheKey = `${entityType}_${entityId}`;
    const cached = this.memberCache.get(cacheKey);
    const now = Date.now();

    if (!forceRefresh && cached && now - cached.timestamp < this.CACHE_TTL) {
      return cached.data;
    }

    const members = await this.dataService.list<Membership>('memberships', {
      field: 'entityId',
      operator: '==',
      value: entityId,
    });
    this.memberCache.set(cacheKey, { data: members, timestamp: now });
    return members;
  }

  async createMembership(
    membership: Omit<Membership, 'id' | 'createdAt' | 'updatedAt'>,
    teamName?: string
  ): Promise<string> {
    // Check if email already exists on this team
    if (membership.email) {
      const duplicate = await runInInjectionContext(this.injector, async () => {
        const membershipsRef = collection(this.firestore, 'memberships');
        const q = query(
          membershipsRef,
          where('email', '==', membership.email),
          where('entityId', '==', membership.entityId)
        );
        const snapshot = await getDocs(q);
        return !snapshot.empty;
      });
      if (duplicate) {
        throw new Error('User already exists on team');
      }
    }

    const membershipId = await this.dataService.create(
      'memberships',
      membership
    );

    // Send invitation email if status is invited
    if (membership.invitationStatus === 'invited' && membership.email) {
      try {
        const sendEmail = httpsCallable(this.functions, 'sendInvitationEmail');
        const appUrl =
          (typeof window !== 'undefined' && window.location?.origin) ||
          'https://omniteams.app';
        const signupLink = `${appUrl}/auth/login?redirect=${encodeURIComponent('/pending-invitations')}`;
        await sendEmail({
          email: membership.email,
          teamName: teamName || 'the team',
          invitedByName: membership.invitedByName || 'A team member',
          role: membership.role || 'member',
          signupLink,
        });
      } catch (error) {
        console.error('Failed to send invitation email:', error);
      }
    }

    return membershipId;
  }

  async addMembership(
    membership: Omit<Membership, 'id' | 'createdAt' | 'updatedAt'>,
    displayName?: string
  ): Promise<void> {
    const membershipId = `${membership.userId}_${membership.entityType}_${membership.entityId}`;
    await this.dataService.merge('memberships', membershipId, {
      ...membership,
      displayName,
    });
  }

  async removeMembership(
    userId: string,
    entityType: string,
    entityId: string
  ): Promise<void> {
    const membershipId = `${userId}_${entityType}_${entityId}`;
    await this.dataService.delete('memberships', membershipId);
  }

  /** Deletes a membership by Firestore document id (works for addDoc-created memberships). */
  async deleteMembershipByDocumentId(membershipDocumentId: string): Promise<void> {
    await this.dataService.delete('memberships', membershipDocumentId);
    this.memberCache.clear();
    this.triggerRefresh();
  }

  hasRole(entityType: string, entityId: string, requiredRole: string): boolean {
    const membership = this.userMemberships().find(
      (m) => m.entityType === entityType && m.entityId === entityId
    );
    return membership?.role === requiredRole;
  }

  hasAnyRole(entityType: string, entityId: string, roles: string[]): boolean {
    const membership = this.userMemberships().find(
      (m) => m.entityType === entityType && m.entityId === entityId
    );
    return membership ? roles.includes(membership.role) : false;
  }

  /**
   * Accept a team/league/org invitation without payment (for coach, manager, player, parent roles).
   * Caller must be authenticated; the membership is updated with the current user's id.
   */
  async acceptInvitationWithoutPayment(membershipId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) throw new Error('Not authenticated');
    await this.dataService.update('memberships', membershipId, {
      userId: user.uid,
      invitationStatus: 'active',
      isActive: true,
    });
    this.memberCache.clear();
    this.triggerRefresh();
  }

  /**
   * Decline a pending invitation. The membership is marked declined and no longer shown as pending.
   */
  async declineInvitation(membershipId: string): Promise<void> {
    await this.dataService.update('memberships', membershipId, {
      invitationStatus: 'declined',
    });
    this.memberCache.clear();
    this.triggerRefresh();
  }

  async activatePendingMemberships(
    userId: string,
    email: string
  ): Promise<void> {
    const snapshot = await runInInjectionContext(this.injector, async () => {
      const membershipsRef = collection(this.firestore, 'memberships');
      const q = query(
        membershipsRef,
        where('email', '==', email),
        where('invitationStatus', '==', 'invited')
      );
      return getDocs(q);
    });

    const updates = snapshot.docs.map(async (docSnapshot) => {
      await this.dataService.update('memberships', docSnapshot.id, {
        userId,
        invitationStatus: 'active',
      });
    });

    await Promise.all(updates);
  }
}
